import { getAxiosClient } from '../../client';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse } from '../../responses';
import { ProductsEndpoints } from './productsEndpoints';
import {
    toProductDetail,
    toProductExportResponse,
    toProductListResponse,
} from './productsMapping';
import type { IProductsService } from './IProductsService';
import type {
    ProductCreateRequest,
    ProductDetailQuery,
    ProductExportQuery,
    ProductListQuery,
    ProductUpdateRequest,
    VariantBarcodesUpdateRequest,
} from './requests';
import type {
    GetProductDetailResponse,
    GetProductExportResponse,
    GetProductListResponse,
} from './responses';

type ProductsServiceOptions = {
    baseUrl: string;
};

export class ProductsService implements IProductsService {
    private readonly baseUrl: string;

    constructor({ baseUrl }: ProductsServiceOptions) {
        this.baseUrl = baseUrl;
    }

    public async getProductList(
        query: ProductListQuery = {},
    ): Promise<ApiResponse<GetProductListResponse>> {
        // bos-api binds query keys case-insensitively, so the camelCase
        // keys go out as they are and still bind to the controller's
        // `Page`, `PageSize`, `Search`, `CategoryId`, `LowStock`, ….
        const url = ProductsEndpoints.list();
        const result = await makeApiRequest<GetProductListResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url, {
                params: query,
            }),
        );
        return result.success
            ? {
                  success: true,
                  response: toProductListResponse(result.response),
              }
            : result;
    }

    public async exportProducts(
        query: ProductExportQuery = {},
    ): Promise<ApiResponse<GetProductExportResponse>> {
        // The list's parameters without the paging, written the list's way
        // (repeated keys, PascalCase on the wire).
        const url = ProductsEndpoints.export();
        const result = await makeApiRequest<GetProductExportResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url, {
                params: query,
            }),
        );
        return result.success
            ? {
                  success: true,
                  response: toProductExportResponse(result.response),
              }
            : result;
    }

    public async getProduct(
        id: string,
        query: ProductDetailQuery = {},
    ): Promise<ApiResponse<GetProductDetailResponse>> {
        const url = ProductsEndpoints.detail(id);
        const client = getAxiosClient({ baseURL: this.baseUrl });
        // No branch, no query string: the request every other caller of the
        // detail (the edit form, the purchase pickers) has always sent.
        const result = await makeApiRequest<GetProductDetailResponse>(
            query.branchId
                ? client.get(url, { params: { branchId: query.branchId } })
                : client.get(url),
        );
        return result.success
            ? { success: true, response: toProductDetail(result.response) }
            : result;
    }

    public async createProduct(
        body: ProductCreateRequest,
    ): Promise<ApiResponse<GetProductDetailResponse>> {
        // POST creates the whole graph (product + variants + batches) in one
        // round-trip; the bos-api returns the persisted ProductDetail (201).
        const url = ProductsEndpoints.create();
        const result = await makeApiRequest<GetProductDetailResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).post(url, body),
        );
        return result.success
            ? { success: true, response: toProductDetail(result.response) }
            : result;
    }

    public async updateProduct(
        id: string,
        body: ProductUpdateRequest,
    ): Promise<ApiResponse<GetProductDetailResponse>> {
        // Fields-only update — product metadata + existing variants' editable
        // fields. The bos-api returns the updated ProductDetail (200).
        const url = ProductsEndpoints.update(id);
        const result = await makeApiRequest<GetProductDetailResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).put(url, body),
        );
        return result.success
            ? { success: true, response: toProductDetail(result.response) }
            : result;
    }

    public async updateVariantBarcodes(
        productId: string,
        variantId: string,
        body: VariantBarcodesUpdateRequest,
    ): Promise<ApiResponse<GetProductDetailResponse>> {
        // The whole set in one PUT: add, remove and make main differ only in
        // the body. The bos-api returns the updated ProductDetail (200).
        const url = ProductsEndpoints.variantBarcodes(productId, variantId);
        const result = await makeApiRequest<GetProductDetailResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).put(url, body),
        );
        return result.success
            ? { success: true, response: toProductDetail(result.response) }
            : result;
    }

    public deleteProduct(id: string): Promise<ApiResponse<void>> {
        const url = ProductsEndpoints.delete(id);
        return makeApiRequest<void>(
            getAxiosClient({ baseURL: this.baseUrl }).delete(url),
        );
    }

    public setProductArchived(
        id: string,
        archived: boolean,
    ): Promise<ApiResponse<void>> {
        // Two routes rather than a PATCH with a flag: archiving and
        // unarchiving are separate acts server-side.
        const url = archived
            ? ProductsEndpoints.archive(id)
            : ProductsEndpoints.unarchive(id);
        return makeApiRequest<void>(
            getAxiosClient({ baseURL: this.baseUrl }).post(url),
        );
    }
}
